export const PAID_TOOLS_PATH = "/pricing#paid-tools";

export type PaidFeatureId =
  | "bulkOutput"
  | "premiumDesigns"
  | "advancedEditor"
  | "multipleBrandKits"
  | "brandKitStyling"
  | "unlimitedBusinessTemplates"
  | "designPacks";

export interface PaidFeature {
  id: PaidFeatureId;
  title: string;
  cta: string;
  lockedSummary: string;
}

export type EntitlementPlan = "free" | "monthly" | "lifetime";
export type EntitlementSource = "none" | "verified_checkout";

export interface EntitlementState {
  plan: EntitlementPlan;
  source: EntitlementSource;
  features: PaidFeatureId[];
}

export const PAID_FEATURES: Record<PaidFeatureId, PaidFeature> = {
  bulkOutput: {
    id: "bulkOutput",
    title: "Spreadsheet document export",
    cta: "Review premium spreadsheet export",
    lockedSummary:
      "Small local preview is available before purchase. Bulk export and saved mappings need Monthly or Lifetime access.",
  },
  premiumDesigns: {
    id: "premiumDesigns",
    title: "Premium invoice designs",
    cta: "Review premium design editing",
    lockedSummary:
      "Manual documents stay free when a special invoice format exists, such as Germany XRechnung/ZUGFeRD or France Factur-X. Custom design tools need Monthly or Lifetime access.",
  },
  advancedEditor: {
    id: "advancedEditor",
    title: "Advanced design editor controls",
    cta: "Review premium advanced editor controls",
    lockedSummary: "Invoice design editor controls need Monthly or Lifetime access.",
  },
  multipleBrandKits: {
    id: "multipleBrandKits",
    title: "Multiple Brand Kits",
    cta: "Review premium Brand Kit library",
    lockedSummary:
      "Free manual documents can use one-off seller details. Saved Brand Kits need Monthly or Lifetime access.",
  },
  brandKitStyling: {
    id: "brandKitStyling",
    title: "One-click Brand Kit styling",
    cta: "Review premium Brand Kit styling",
    lockedSummary:
      "Manual style choices stay free. One-click Brand Kit application needs paid access.",
  },
  unlimitedBusinessTemplates: {
    id: "unlimitedBusinessTemplates",
    title: "Reusable preset library",
    cta: "Review premium tools",
    lockedSummary:
      "Free manual documents do not need saved reusable presets. Reusable preset libraries need Monthly or Lifetime access.",
  },
  designPacks: {
    id: "designPacks",
    title: "Design pack tools",
    cta: "Review premium design pack tools",
    lockedSummary: "Direct design JSON import/export needs paid access.",
  },
};

export const CURRENT_ENTITLEMENT_STATE: EntitlementState = {
  plan: "free",
  source: "none",
  features: [],
};

export function paidFeatureCta(id: PaidFeatureId): string {
  return PAID_FEATURES[id].cta;
}

export function paidFeatureTitle(id: PaidFeatureId): string {
  return PAID_FEATURES[id].title;
}

export function isPaidFeatureUnlocked(
  id: PaidFeatureId,
  state: EntitlementState = CURRENT_ENTITLEMENT_STATE,
): boolean {
  return (
    state.source === "verified_checkout" && state.plan !== "free" && state.features.includes(id)
  );
}

export function lockedPaidFeatureSummary(id: PaidFeatureId): string {
  return PAID_FEATURES[id].lockedSummary;
}
